import { prepareForSlot } from '@mui/base'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { Link, Rating, Skeleton } from 'base-ui'

import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { NextLink } from '../../../next-link'
import { TosSuperscriptHowProductRatingCalculated } from '../../../tos-superscripts'
import { urlGetRelativeUrl } from '../../../url-handling'
import { gkAirAccessorGetLocalizedProductUrl } from '../../model/gkAirAccessorGetLocalizedProductUrl'
import { gkAirAccessorGetSponsoredProductLink } from '../../model/gkAirAccessorGetSponsoredProductLink'
import { useGkAirAreaContext } from '../GkAirAreaProvider'

const LinkSlot = prepareForSlot(NextLink)

type GkAirProductRatingProps = {
  product?: GkAirRecommendedProductDataWeb
}

export const GkAirProductRating: FC<GkAirProductRatingProps> = ({
  product,
}) => {
  const { locale } = useGlobalConfigContext()
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const { metadata } = useGkAirAreaContext()
  const intl = useIntl()

  if (!product) {
    return (
      <div className="flex min-h-[var(--type-scale-xl-font-size)] flex-col items-center">
        <Skeleton className="w-[106px]" />
        <Skeleton className="mt-2 w-[40px]" />
      </div>
    )
  }

  const rating = product.rating_average
    ? Math.round(Number(product.rating_average))
    : 0

  if (rating === 0) {
    return (
      <div className="invisible min-h-[calc(var(--type-scale-xl-font-size)+var(--type-scale-l-line-height)+var(--spacing-basic-4xs))]" />
    )
  }

  const sponsoredProductLink = gkAirAccessorGetSponsoredProductLink({
    metadata,
    product,
  })
  const href = sponsoredProductLink
    ? `${sponsoredProductLink}#user-reviews-section`
    : `${urlGetRelativeUrl(
        gkAirAccessorGetLocalizedProductUrl({
          locale,
          product,
        }),
      )}#user-reviews-section`

  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex">
        <Link
          aria-label={intl.formatMessage({ id: 'product.rate' }, { rating })}
          className="flex"
          data-qa-id="product-rating"
          href={href}
          onClick={trackClick}
          rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
          slots={{ root: LinkSlot }}
        >
          <Rating
            className="text-xl"
            readOnly
            slotProps={{
              iconContainer: {
                className: '-mx-0.5',
              },
            }}
            value={rating}
          />
        </Link>
        <TosSuperscriptHowProductRatingCalculated className="top-1" />
      </span>
      <Link
        aria-label={intl.formatMessage(
          { id: 'general.label.numberOfRatings' },
          { numberOfRatings: Number(product.rating_count) },
        )}
        className="leading-l text-dark-primary-max"
        data-qa-id="product-rating-count"
        href={href}
        onClick={trackClick}
        rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
        slots={{ root: LinkSlot }}
      >{`(${Number(product.rating_count)})`}</Link>
    </span>
  )
}
